import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import { Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { savePaciente, prepareContactosEmergenciaForStorage } from '../data/consultorioData';
import FichaOdontologicaForm, {
  getInitialFichaForm,
  buildDireccionResumen,
  buildHistoriaMedicaForStorage,
} from '../components/Consultorio/FichaOdontologicaForm';
import { getAntecedentesOdontoDetallesFaltantes } from '../components/Consultorio/fichaOdontologicaLecturaHelpers';

export const PACIENTES_CHANGED_EVENT = 'consultorio-pacientes-changed';

const NuevoPacienteContext = createContext(null);

export function useNuevoPaciente() {
  const ctx = useContext(NuevoPacienteContext);
  if (!ctx) {
    throw new Error('useNuevoPaciente debe usarse dentro de NuevoPacienteProvider');
  }
  return ctx;
}

export function NuevoPacienteProvider({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const idDoctor = user?.id;

  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(() => getInitialFichaForm(null));
  const [fichaAccordionMount, setFichaAccordionMount] = useState(0);
  const [saving, setSaving] = useState(false);
  const onSavedRef = useRef(null);

  const close = useCallback(() => {
    setShow(false);
    setEditing(null);
    onSavedRef.current = null;
  }, []);

  const openNuevoPaciente = useCallback((options = {}) => {
    onSavedRef.current = options.onSaved || null;
    setEditing(null);
    setForm(getInitialFichaForm(null));
    setFichaAccordionMount((n) => n + 1);
    setShow(true);
  }, []);

  const openEditarPaciente = useCallback((paciente, options = {}) => {
    onSavedRef.current = options.onSaved || null;
    setEditing(paciente);
    setForm(getInitialFichaForm(paciente));
    setFichaAccordionMount((n) => n + 1);
    setShow(true);
  }, []);

  const nombreTrim = (form.nombre || '').trim();
  const cedTrim = (form.cedula || '').toString().trim();
  const telTrim = (form.telefono || '').toString().trim();
  const puedeGuardar = !!(
    nombreTrim ||
    cedTrim ||
    telTrim ||
    (editing && String(editing.nombre || '').trim())
  );

  const handleSave = async () => {
    if (!idDoctor || !puedeGuardar) return;

    const tipoIdentificacionSalida =
      (form.tipoIdentificacion || '').trim() ||
      (cedTrim ? 'Cédula' : '') ||
      (editing && (editing.tipoIdentificacion || '').trim()) ||
      '';

    const nombreSalida =
      nombreTrim ||
      (cedTrim ? `Paciente (${cedTrim})` : '') ||
      (telTrim ? 'Paciente (completar nombre)' : '') ||
      (editing && String(editing.nombre || '').trim()) ||
      '';

    if (!nombreSalida) return;

    const antecedentesSinDetalle = getAntecedentesOdontoDetallesFaltantes(form, idDoctor);
    if (antecedentesSinDetalle.length) {
      window.alert(
        `Complete el detalle de los antecedentes marcados como «Sí»:\n\n• ${antecedentesSinDetalle.join('\n• ')}`,
      );
      return;
    }

    const toSave = {
      ...editing,
      ...form,
      nombre: nombreSalida,
      tipoIdentificacion: tipoIdentificacionSalida,
      ...prepareContactosEmergenciaForStorage(form),
      ...buildHistoriaMedicaForStorage(form, idDoctor),
      direccion: buildDireccionResumen(form),
      fechaNacimiento: form.fechaNacimiento || null,
      fechaUltimaVisitaOdontologica: form.fechaUltimaVisitaOdontologica || null,
      consentimientoInformado: !!form.consentimientoInformado,
      consentimientoInformadoFecha: form.consentimientoInformadoFecha || null,
      consentimientoInformadoTexto: form.consentimientoInformadoTexto || '',
      consentimientoInformadoFirmaDataUrl: form.consentimientoInformadoFirmaDataUrl || '',
      consentimientoInformadoFirmaStorageId: form.consentimientoInformadoFirmaStorageId || '',
      consentimientoInformadoFirmaModo: form.consentimientoInformadoFirmaModo || '',
      consentimientoInformadoFirmaBytes: form.consentimientoInformadoFirmaBytes || 0,
    };

    setSaving(true);
    try {
      const saved = await savePaciente(toSave, idDoctor);
      window.dispatchEvent(new CustomEvent(PACIENTES_CHANGED_EVENT));
      close();
      const cb = onSavedRef.current;
      if (typeof cb === 'function') {
        cb(saved);
      } else if (saved?.id) {
        navigate(`/consultorio/paciente/${saved.id}`);
      }
    } catch (e) {
      console.error(e);
      window.alert('No se pudo guardar el paciente. Intente de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  const value = {
    openNuevoPaciente,
    openEditarPaciente,
    closeNuevoPaciente: close,
  };

  return (
    <NuevoPacienteContext.Provider value={value}>
      {children}
      <Modal
        show={show}
        onHide={close}
        size="xl"
        centered
        className="consultorio-modal ficha-modal"
        backdrop="static"
        animation={false}
        enforceFocus={false}
        dialogClassName="ficha-modal-dialog"
      >
        <Modal.Header closeButton className="ficha-modal-header">
          <Modal.Title>
            <i className={`bi ${editing ? 'bi-pencil-square' : 'bi-person-plus-fill'}`} />
            {editing ? 'Editar paciente' : 'Registrar paciente'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="ficha-modal-body-wrap p-0">
          <div className="ficha-odonto-modal-body">
            <FichaOdontologicaForm
              form={form}
              setForm={setForm}
              idDoctor={idDoctor}
              tenantId={user?.tenant_id}
              accordionMountKey={fichaAccordionMount}
              inModal
              usuarioProfesionalEmail={user?.email || ''}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="cp-btn cp-btn--ghost cp-btn--sm"
            onClick={close}
            disabled={saving}
          >
            Cancelar
          </button>
          <button
            type="button"
            title="Guarda con datos mínimos; podés completar el resto al editar."
            className="cp-btn cp-btn--primary cp-btn--sm"
            onClick={handleSave}
            disabled={!puedeGuardar || saving}
          >
            <i className={`bi ${saving ? 'bi-hourglass-split' : 'bi-cloud-check'}`} />
            {saving ? 'Guardando…' : editing ? 'Guardar cambios' : 'Registrar'}
          </button>
        </Modal.Footer>
      </Modal>
    </NuevoPacienteContext.Provider>
  );
}
